<template>
  <TransitionRoot as="template" :show="open">
    <Dialog as="div" class="relative z-50" @close="onSkip">
      <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0" enter-to="opacity-100"
        leave="ease-in duration-200" leave-from="opacity-100" leave-to="opacity-0">
        <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
      </TransitionChild>

      <div class="fixed inset-0 z-40 overflow-y-auto">
        <div class="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
          <TransitionChild as="template" enter="ease-out duration-300"
            enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enter-to="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-200"
            leave-from="opacity-100 translate-y-0 sm:scale-100"
            leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
            <DialogPanel
              class="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">

              <div class="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                <div class="sm:flex sm:items-start">
                  <div class="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-yellow-100 sm:mx-0 sm:h-10 sm:w-10">
                    <Icon name="heroicons:exclamation-triangle" class="h-6 w-6 text-yellow-600" />
                  </div>
                  <div class="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left w-full">
                    <DialogTitle as="h3" class="text-base font-semibold leading-6 text-gray-900">
                      Eligible Fees Found
                    </DialogTitle>
                    <div class="mt-2">
                      <div v-if="loading" class="flex items-center justify-center py-4">
                        <LoadingCircle size="8" />
                        <span class="ml-2 text-gray-500">Checking eligible fees...</span>
                      </div>
                      <div v-else>
                        <p class="text-sm text-gray-500">
                          The selected program(s) have the following eligible fee(s) for this student:
                        </p>

                        <!-- Programs and Fees List -->
                        <div class="mt-4 max-h-60 overflow-y-auto">
                          <div v-for="program in programsWithFees" :key="program.clubId" class="mb-3">
                            <p class="text-sm font-medium text-gray-700">{{ program.clubName }}</p>
                            <div v-if="program.eligibleFees && program.eligibleFees.length > 0" class="mt-1 ml-4">
                              <div v-for="fee in program.eligibleFees" :key="fee.id" class="flex items-center mb-1">
                                <input
                                  type="checkbox"
                                  :id="`fee-${fee.id}`"
                                  :checked="isSelected(program.clubId, fee.id)"
                                  @change="toggleFeeSelection(program.clubId, fee.id)"
                                  class="h-4 w-4 rounded border-gray-300 text-brand-color focus:ring-brand-color"
                                />
                                <label :for="`fee-${fee.id}`" class="ml-2 text-sm text-gray-600">
                                  {{ fee.name }} - ${{ fee.amount?.toFixed(2) }}
                                  <span class="text-gray-400">({{ fee.type === 'RECURRING' ? 'Recurring' : 'One-time' }})</span>
                                </label>
                              </div>
                            </div>
                            <div v-else class="mt-1 ml-4">
                              <span class="text-sm text-gray-400 italic">None</span>
                            </div>
                          </div>
                        </div>

                        <p class="text-sm text-gray-500 mt-3">
                          Would you like to connect these fee(s) to the student?
                        </p>
                        <p class="text-sm text-gray-400 mt-2 italic">
                          If you do, invoices will be created based on each fee's invoice date.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                <button
                  type="button"
                  :disabled="loading || processing || selectedFeesCount === 0"
                  @click="onProceed"
                  class="inline-flex w-full justify-center rounded-md bg-brand-color px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-brand-color-800 sm:ml-3 sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed">
                  <LoadingCircle v-if="processing" size="5" class="mr-2" />
                  {{ processing ? 'Connecting...' : `Proceed (${selectedFeesCount})` }}
                </button>
                <button
                  type="button"
                  :disabled="processing"
                  @click="onSkip"
                  class="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto disabled:opacity-50">
                  No, skip the fees
                </button>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { Dialog, DialogPanel, DialogTitle, TransitionChild, TransitionRoot } from '@headlessui/vue';
import {
  eligibleFeesWarningDialogue,
  useGlobalEvent,
  useGlobalEventLister,
  useNotificationEvent
} from "~/composables/useEventBus";
import { feeStore } from "~/store/feeStore";
import { companyStore } from "~/store/companyStore";
import { userStore } from "~/store/userStore";
import { storeToRefs } from "pinia";
import type { NotificationDetails } from "~/models/notificationDetails";
import { NotificationType } from "~/utils/enums/NotificationType";

interface EligibleFee {
  id: string;
  name: string;
  type: string;
  amount: number;
}

interface ProgramWithFees {
  clubId: number;
  clubName: string;
  studentEnrollmentType: string;
  eligibleFees: EligibleFee[];
}

const useFeeStore = feeStore();
const useCompanyStore = companyStore();
const useUserStore = userStore();
const { getCompanyId } = storeToRefs(useCompanyStore);
const { getLoggedInUser } = storeToRefs(useUserStore);

const open = ref(false);
const loading = ref(false);
const processing = ref(false);
const programsWithFees = ref<ProgramWithFees[]>([]);
const studentId = ref<number | null>(null);
const selectedFees = ref<Map<string, { clubId: number; clubFeeId: string }>>(new Map());

const selectedFeesCount = computed(() => selectedFees.value.size);

const isSelected = (clubId: number, feeId: string): boolean => {
  const key = `${clubId}-${feeId}`;
  return selectedFees.value.has(key);
};

const toggleFeeSelection = (clubId: number, feeId: string) => {
  const key = `${clubId}-${feeId}`;
  if (selectedFees.value.has(key)) {
    selectedFees.value.delete(key);
  } else {
    selectedFees.value.set(key, { clubId, clubFeeId: feeId });
  }
  // Force reactivity
  selectedFees.value = new Map(selectedFees.value);
};

const onProceed = async () => {
  if (selectedFees.value.size === 0) {
    onSkip();
    return;
  }

  processing.value = true;
  try {
    const fees = Array.from(selectedFees.value.values());

    const response = await useFeeStore.connectStudentToFees({
      studentId: studentId.value!,
      fees: fees,
      companyId: getCompanyId.value,
      createInvoices: true,
      createdBy: getLoggedInUser.value.id
    });

    if (response.IsOperationSuccessful) {
      const emailsMsg = response.Result?.emailsSent > 0 ? ` ${response.Result.emailsSent} email(s) sent.` : '';
      useGlobalEvent(useNotificationEvent(), <NotificationDetails>{
        type: NotificationType.SUCCESS,
        title: 'Success',
        message: `${response.Result?.connectedCount || fees.length} fee(s) connected to student. ${response.Result?.invoicesCreated || 0} invoice(s) created.${emailsMsg}`,
      });
    } else {
      useGlobalEvent(useNotificationEvent(), <NotificationDetails>{
        type: NotificationType.ERROR,
        title: 'Error',
        message: response.Message || 'Failed to connect fees to student',
      });
    }
  } catch (error) {
    console.error('Error connecting fees to student:', error);
    useGlobalEvent(useNotificationEvent(), <NotificationDetails>{
      type: NotificationType.ERROR,
      title: 'Error',
      message: 'An error occurred while connecting fees to the student',
    });
  } finally {
    processing.value = false;
    closeDialog();
  }
};

const onSkip = () => {
  if (processing.value) return;
  closeDialog();
};

const closeDialog = () => {
  open.value = false;
  programsWithFees.value = [];
  studentId.value = null;
  selectedFees.value = new Map();
};

const fetchEligibleFees = async (sId: number, clubIds: number[]) => {
  loading.value = true;
  try {
    const response = await useFeeStore.getEligibleFeesForStudent({
      studentId: sId,
      clubIds: clubIds,
      companyId: getCompanyId.value
    });

    if (response.IsOperationSuccessful && response.Result) {
      programsWithFees.value = response.Result.programs || [];

      // Auto-select all eligible fees by default
      selectedFees.value = new Map();
      for (const program of programsWithFees.value) {
        if (program.eligibleFees && program.eligibleFees.length > 0) {
          for (const fee of program.eligibleFees) {
            const key = `${program.clubId}-${fee.id}`;
            selectedFees.value.set(key, { clubId: program.clubId, clubFeeId: fee.id });
          }
        }
      }
    }
  } catch (error) {
    console.error('Error fetching eligible fees:', error);
  } finally {
    loading.value = false;
  }
};

const hasAnyEligibleFees = computed(() => {
  return programsWithFees.value.some(p => p.eligibleFees && p.eligibleFees.length > 0);
});

function setupEventListener() {
  useGlobalEventLister(eligibleFeesWarningDialogue(), async function (data: any) {
    if (data && data.studentId && data.clubIds && data.clubIds.length > 0) {
      studentId.value = data.studentId;
      open.value = true;
      await fetchEligibleFees(data.studentId, data.clubIds);

      // If no eligible fees found, close dialog automatically
      if (!hasAnyEligibleFees.value) {
        closeDialog();
      }
    }
  });
}

onMounted(() => {
  setupEventListener();
});
</script>
